import React, { useMemo, useRef, Suspense, useState } from "react";
import * as survey from "@aidkitorg/types/lib/survey";
import { getDistroBrowserIncompatibility, safeParse } from "../../Util";
import { usePost } from "../../API";
import { DataTable, Select, Button, useToast, GroupByPanel, GroupedRowActionContext, GroupedRowLabelContext, ActionsDropdown, Icon, nonHookMarkdown, SelectOptionType } from "@aidkitorg/component-library";
import { FieldOption, GroupByConfig } from "@aidkitorg/types/lib/admin-panel/filters";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { useAsyncJobStatusBatch } from "../../Hooks/AsyncJobStatusCheck";
import { useAsyncInterval } from "../../Util";
import type { ColumnDef } from "@tanstack/react-table";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));
const EMPTY_GENERATOR: survey.ConversationGenerator = { name: "", steps: [] };

export function ConversationGenerationConfig() {
  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);

  const { toast } = useToast();
  const listGenerators = usePost('/ai/trace_generation/list');
  const registerGenerator = usePost('/ai/trace_generation/register');
  const queueTrace = usePost('/ai/trace_generation/queue');
  const listConversations = usePost('/ai/trace_generation/conversations');
  const getTraceSummary = usePost('/ai/trace_generation/summary');
  const getPlan = usePost('/ai/trace_generation/plan');
  const getDynamicPlan = usePost('/ai/trace_generation/dynamic_plan');
  const getInteractionsCSV = usePost('/ai/trace_generation/interactions_csv', { textOnly: true });
  type ListConversationsResponse = Awaited<ReturnType<typeof listConversations>>;
  type TraceConversation = NonNullable<ListConversationsResponse["outcomes"]>[number];
  type TraceOutcomeStep = TraceConversation["outcome"][number]["steps"][number];
  type TraceTreeRow = {
    runId: string;
    status: string;
    conversationId: string;
  } & Record<number, TraceOutcomeStep>;
  type StepValuePresentation = {
    groupingValue: string;
    cellValue: React.ReactNode;
  };

  const [generator, setGenerator] = useState<survey.ConversationGenerator>(EMPTY_GENERATOR);

  const [generatorList, setGeneratorList] = useState<survey.ConversationGenerator[]>([]);
  const [traceConversations, setTraceConversations] = useState<TraceConversation[]>([]);
  const [traceConversationsLoading, setTraceConversationsLoading] = useState(false);
  const [traceConversationsError, setTraceConversationsError] = useState<string | null>(null);
  const [traceSummary, setTraceSummary] = useState<Record<string, number>>({});
  const [traceSummaryError, setTraceSummaryError] = useState<string | null>(null);
  const { addTaskIds, clearTaskIds, hasActiveTasks, activeTaskIds } = useAsyncJobStatusBatch(3_000);
  const [plan, setPlan] = useState<number>();

  const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);
  const [groupByConfig, setGroupByConfig] = useState<GroupByConfig>({ enabled: false });
  const [hiddenCols, setHiddenCols] = useState<string[]>([]);

  const getStepText = (items: TraceOutcomeStep["items"]) =>
    items
      .flatMap((item) => item.type === "message" ? item.content : [])
      .map((contentPart) => contentPart.type === "text" ? contentPart.content : contentPart.url)
      .join(" ");

  const getStepValuePresentation = (step: TraceOutcomeStep | undefined): StepValuePresentation => {
    if (!step) {
      return {
        groupingValue: "[Missing Step Data]",
        cellValue: "[Missing Step Data]",
      };
    }

    const { block, items } = step;

    switch (block.kind) {
      case "Single Static Content Block":
        return {
          groupingValue: block.content.content,
          cellValue: block.content.content,
        };
      case "Prompt Gen Block": {
        const promptText = getStepText(items);
        return {
          groupingValue: promptText,
          cellValue: promptText,
        };
      }
      case "Item Ref Block": {
        const [item] = items;
        if (!item) {
          return {
            groupingValue: "Item Ref Block: [Missing Item]",
            cellValue: "[Missing Item Ref Data]",
          };
        }

        if (item.type === "appState") {
          const targetField = item.appState.currentQuestion?.targetField;
          const fieldLabel = targetField ?? "unknown_field";
          const rawStateValue = targetField ? item.appState.info?.[targetField] : undefined;
          const stateValue = rawStateValue ?? '"empty"';
          const stateValueText = String(stateValue);
          const questionContent = item.appState.currentQuestion?.content ?? "";
          return {
            groupingValue: `[App State] (${fieldLabel}: ${stateValueText})`,
            cellValue: (
              <div className="space-y-1">
                <div className="text-xs text-gray-700">
                  <strong>[App State]</strong> ({fieldLabel}: {stateValueText})
                </div>
                <div className="text-xs text-gray-800 [&_p]:m-0">
                  {nonHookMarkdown(questionContent)}
                </div>
              </div>
            ),
          };
        }

        return {
          groupingValue: item.type,
          cellValue: item.type,
        };
      }
      default:
        return {
          groupingValue: block.kind,
          cellValue: block.kind,
        };
    }
  };

  const generatorReferences = useMemo(() => generatorList
    .filter(g => g.name !== generator.name)
    .map(g => g.name),
  [generatorList, generator]);

  useAsyncEffect(async () => {
    setPlan(await getDynamicPlan({ steps: generator.steps }));
  }, [generator]);

  useAsyncEffect(async () => {
    const generators = await listGenerators({});

    setGeneratorList(
      generators.map(({ id, config: gen }) => ({
        id,
        name: gen.name!,
        steps: gen.steps,
        ...(gen.rubrics ? { rubrics: gen.rubrics } : {})
      }))
    );

    distroRef.current?.initialize(
      generator, undefined, undefined, undefined, { generators: generators.map(g => g.config.name) }
    );
  }, []);

  useAsyncEffect(async (signal) => {
    if (!generator.id) {
      setTraceConversations([]);
      setTraceConversationsError(null);
      setTraceSummary({});
      setTraceSummaryError(null);
      clearTaskIds();
      setPlan(0);
      return;
    }

    setPlan(await getPlan({
      scenarioId: generator.id
    }));

    setTraceConversationsLoading(true);
    setTraceConversationsError(null);
    try {
      const [conversationsResponse, summaryResponse] = await Promise.all([
        listConversations({ scenarioId: generator.id }, signal),
        getTraceSummary({ scenarioId: generator.id }, signal)
      ]);
      if (!signal.aborted) {
        setTraceConversations(conversationsResponse.outcomes ?? []);
        setTraceSummary(summaryResponse.counts ?? {});
        setTraceSummaryError(null);
      }
    } catch (error) {
      if (!signal.aborted) {
        setTraceConversationsError("Failed to load conversations.");
        setTraceConversations([]);
        setTraceSummaryError("Failed to load summary.");
        setTraceSummary({});
      }
    } finally {
      if (!signal.aborted) {
        setTraceConversationsLoading(false);
      }
    }
  }, [generator.id, listConversations, getTraceSummary, clearTaskIds]);

  useAsyncInterval(async () => {
    if (!generator.id || !hasActiveTasks) return;
    try {
      const summaryResponse = await getTraceSummary({ scenarioId: generator.id });
      setTraceSummary(summaryResponse.counts ?? {});
      setTraceSummaryError(null);
    } catch (error) {
      setTraceSummaryError("Failed to refresh summary.");
    }
  }, 3_000);

  useAsyncEffect(async (signal) => {

    if (!generator.id) return;

    const conversationsResponse = await listConversations({ scenarioId: generator.id }, signal);

    setTraceConversations(conversationsResponse.outcomes ?? []);

  }, [activeTaskIds]);

  const generatorOptions = useMemo((): SelectOptionType<survey.ConversationGenerator>[] =>
    generatorList.map((gen) => ({
      label: gen.name,
      value: gen
    })),
  [generatorList]);

  const [selectedGeneratorOption, setSelectedGeneratorOption] = useState<SelectOptionType<survey.ConversationGenerator> | undefined>();

  const handleGeneratorSelect = (option: SelectOptionType<survey.ConversationGenerator> | undefined) => {
    const gen = option?.value ?? EMPTY_GENERATOR;
    distroRef.current?.initialize(
      gen, undefined, undefined, undefined, { generators: generatorReferences }
    );
    setSelectedGeneratorOption(option);
  };

  const handleRegister = async () => {
    const { id } = await registerGenerator({
      id: generator.id,
      config: {
        steps: generator.steps,
        name: generator.name,
        rubrics: generator.rubrics
      },
    });

    distroRef.current?.initialize(
      { ...generator, id }, undefined, undefined, undefined, { generators: generatorReferences }
    );

    const generators = await listGenerators({});

    setGeneratorList(
      generators.map(({ id, config: gen }) => ({
        id,
        name: gen.name!,
        steps: gen.steps,
        ...(gen.rubrics ? { rubrics: gen.rubrics } : {})
      }))
    );

    setPlan(await getPlan({
      scenarioId: id
    }));

    toast({
      title: 'Updated',
      description: 'Updated config!'
    });
  };

  const handleQueue = async () => {
    if (!generator.id) return;
    setTraceConversationsLoading(true);
    setTraceConversationsError(null);
    setTraceSummaryError(null);
    try {
      const queueResult = await queueTrace({ scenarioId: generator.id });
      const [conversationsResponse, summaryResponse] = await Promise.all([
        listConversations({ scenarioId: generator.id }),
        getTraceSummary({ scenarioId: generator.id })
      ]);
      setTraceConversations(conversationsResponse.outcomes ?? []);
      setTraceSummary(summaryResponse.counts ?? {});
      if (queueResult?.taskId) {
        addTaskIds([queueResult.taskId]);
      }
      toast({
        title: 'Queued',
        description: `${plan || null} Runs Queued`
      });
    } catch (error) {
      setTraceConversationsError("Failed to queue or refresh conversations.");
      setTraceSummaryError("Failed to refresh summary.");
    } finally {
      setTraceConversationsLoading(false);
    }
  };

  const statusOrder = ["done", "failed", "pending", "ingesting", "unknown"];
  const statusPillClass = (status: string) => {
    switch (status) {
      case "done":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "pending":
      case "ingesting":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const summaryCount = (status: string) => traceSummary[status] ?? 0;

  const traceTreeData: TraceTreeRow[] = useMemo(() =>
    traceConversations.flatMap(c =>
      c.outcome.map(outcome => {

        const steps = Object.fromEntries(outcome.steps.map((o, i) => [i, o]))

        return {
          status: c.status,
          runId: c.runId,
          conversationId: outcome.conversationId,
          ...steps
        } satisfies TraceTreeRow
      })
    )
  , [traceConversations]);


  const columnDefs = useMemo((): ColumnDef<TraceTreeRow>[] => {
    const baseColumns: ColumnDef<TraceTreeRow>[] = [
      {
        id: 'runId',
        header: "Run ID",
        accessorKey: "runId",
        getGroupingValue(row) {
          return `Run: ${row.runId}`;
        }
      },
      {
        id: 'status',
        header: "Status",
        accessorKey: "status"
      },
      {
        id: "conversationId",
        header: "Conversation ID",
        accessorKey: "conversationId"
      }
    ];

    const stepColumns: ColumnDef<TraceTreeRow>[] = generator.steps.map((s, i) => {

      return {
        id: String(i),
        accessorKey: String(i),
        header: 'stepLabel' in s ? s.stepLabel! : `Step ${i}`,
        getGroupingValue(row) {
          return getStepValuePresentation(row[i]).groupingValue;
        },
        cell({ row: { original } }) {
          return getStepValuePresentation(original[i]).cellValue;
        }
      }
    });

    return baseColumns.concat(stepColumns);

  }, [generator]);

  const groupByFieldOptions = useMemo((): FieldOption[] => {
    const baseOptions: FieldOption[] = [
      { key: "runId", label: "Run ID", type: "text" },
      { key: "status", label: "Status", type: "text" },
    ]

    const stepOptions: FieldOption[] =
      generator.steps.map((s, i) => ({
        key: String(i),
        label: 'stepLabel' in s ? s.stepLabel! : `Step ${i}`,
        type: "text"
      }))

    return baseOptions.concat(stepOptions);
  }, [generator]);

  const statusSummary = useMemo(() => {
    const total = traceConversations.length;
    const counts = traceConversations.reduce<Record<string, number>>((acc, row) => {
      const key = row.status || "unknown";
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
    return { total, counts };
  }, [traceConversations]);

  const actualHiddenCols = useMemo(() =>
    Array.from(new Set(
      hiddenCols.concat(
        groupByConfig.enabled && groupByConfig.rules?.map(r => r.field) || []
      )
    ))
  , [hiddenCols, groupByConfig]);

  function handleGroupByStep() {

    const stepFields = groupByFieldOptions
      .filter(f => !isNaN(parseInt(f.key)))
      .sort()
      .slice(0, -1);

    setGroupByConfig({
      enabled: true,
      rules: stepFields.map(f => ({
        field: f.key,
        direction: 'asc'
      }))
    });
  }

  const downloadCSV = async () => {
    try {
      setDownloadInProgress(true);
      const data: any = await getInteractionsCSV({ scenarioId: generator.id! });
      const respJSON = safeParse(data, {});
      if (respJSON?.error) {
        throw new Error(respJSON.error);
      }
      const blob = new Blob([data as any], { type: "text/csv" });
      const elem = window.document.createElement('a');
      elem.href = window.URL.createObjectURL(blob);
      const date = new Date();
      elem.download = `interactions_${generator.name ?? generator.id}_${new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
        .replace('T', '_')
        .replace(/:/g, '-')}`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    }
    catch (err: any) {
      console.error(err);
      toast({
        description: `Error downloading trace interactions: ${err?.message ?? "Unknown error"}`,
        variant: 'error'
      });
    }
    finally {
      setDownloadInProgress(false);
    }
  };

  function openReview(conversationId: string) {
    window.open(`/ai/config/review/${conversationId}`, '_blank');
  }

  function openReviewGroup(conversationIds: string[]) {
    const queryParams = new URLSearchParams();
    conversationIds.forEach((conversationId) => {
      queryParams.append("conversationId", conversationId);
    });
    window.open(`/ai/config/review?${queryParams.toString()}`, "_blank");
  }

  function openReviewGroupByTags(tags: string[]) {
    const queryParams = new URLSearchParams();
    tags.forEach((tag) => {
      queryParams.append("tag", tag);
    });
    window.open(`/ai/config/review?${queryParams.toString()}`, "_blank");
  }

  function renderGroupedRowActions({ rows, groupingColumnId, row }: GroupedRowActionContext<TraceTreeRow>) {
    if (groupingColumnId === "runId" && row.getValue(groupingColumnId)) {
      const runTag = `scenario_run:${String(row.getValue(groupingColumnId))}`;
      return (
        <ActionsDropdown
          item={[runTag]}
          className="p-1.5 rounded"
          triggerIcon={<Icon size="sm" name="EllipsisHorizontalIcon" type="solid" role="decorative" />}
          actions={[
            {
              label: `Review Group (${rows.length})`,
              icon: "MagnifyingGlassIcon",
              onClick: (tags) => openReviewGroupByTags(tags)
            }
          ]}
        />
      );
    }

    const reviewableConversationIds = Array.from(new Set(
      rows
        .filter((row) => row.conversationId)
        .map((row) => row.conversationId)
    ));

    if (reviewableConversationIds.length === 0) {
      return null;
    }

    return (
      <ActionsDropdown
        item={reviewableConversationIds}
        className="p-1.5 rounded"
        triggerIcon={<Icon size="sm" name="EllipsisHorizontalIcon" type="solid" role="decorative" />}
        actions={[
          {
            label: `Review Group (${reviewableConversationIds.length})`,
            icon: "MagnifyingGlassIcon",
            onClick: (conversationIds) => openReviewGroup(conversationIds)
          }
        ]}
      />
    );
  }

  function renderGroupedRowLabel({ rows, groupingColumnId }: GroupedRowLabelContext<TraceTreeRow>) {
    const stepIndex = Number(groupingColumnId);
    if (Number.isNaN(stepIndex)) {
      return undefined;
    }

    const firstStep = rows[0]?.[stepIndex];
    return getStepValuePresentation(firstStep).cellValue;
  }

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  return (
    <div className="bg-gray-100 h-full p-4">
      <h1>Conversation Generation</h1>

      <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg space-y-4 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900">Generator Config</h2>
            <p className="text-sm text-gray-500">Load a generator, edit steps, then register and queue conversations.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="mb-3">
              <Select
                label="Generator"
                hideLabel
                description="Generator Selection"
                showDescription
                options={generatorOptions}
                value={selectedGeneratorOption}
                onChange={handleGeneratorSelect}
                clearable
                searchable
                className="w-56"
              />
            </div>
            <Button isDisabled={!generator.name} onClick={handleRegister}>{generator.id ? "Update" : "Register"}</Button>
            <Button onClick={handleQueue} isDisabled={!generator.id || !plan}>Queue {plan ? ` (${plan})` : ''}</Button>
          </div>
        </div>

        <div className="rounded-md border border-gray-200">
          <Suspense fallback={<div className="p-4 text-sm text-gray-500">Loading editor...</div>}>
            <Distro
              ref={distroRef}
              value={generator}
              types="src/survey.ts"
              llmContext={""}
              name="ConversationGenerator"
              onChange={(v) => setGenerator(v)}
              references={{ generators: generatorReferences }}
            />
          </Suspense>
        </div>
      </div>

      <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900">Conversation Batch Status</h2>
            <p className="text-sm text-gray-500">
              {generator.id ? "Live status for runs created by this scenario." : "Load a generator to see run status."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-sm font-medium text-blue-800">
              Pending: {summaryCount("pending")}
            </span>
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-sm font-medium text-amber-800">
              Running: {summaryCount("running")}
            </span>
          </div>
        </div>
        {traceSummaryError && (
          <div className="mt-2 text-sm text-red-600">{traceSummaryError}</div>
        )}
      </div>

      <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg space-y-4 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Conversations
              {generator.id ? ` (${statusSummary.total} runs)` : ""}
            </h2>
            <p className="text-sm text-gray-500">
              {generator.id
                ? "Conversations grouped by run and concrete block."
                : "Load a generator to view recent conversations."}
            </p>
          </div>
          <div className="flex space-x-2">
            <div className="flex-shrink">
              {groupByFieldOptions.length > 0 && (
                <GroupByPanel
                  availableFields={groupByFieldOptions}
                  value={groupByConfig}
                  onConfirm={setGroupByConfig}
                />
              )}
            </div>
            <Button
              variant="secondary"
              onClick={handleGroupByStep}>
              Group by Step
            </Button>
            <Button
              variant="secondary"
              isDisabled={!traceTreeData.length || downloadInProgress}
              icon={<Icon name="ArrowDownTrayIcon" />}
              loading={downloadInProgress}
              onClick={downloadCSV}>
              CSV
            </Button>
          </div>
        </div>

        {traceConversationsError && (
          <div className="text-sm text-red-600">{traceConversationsError}</div>
        )}

        {!!generator.id && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            {statusOrder
              .filter((status) => statusSummary.counts[status])
              .map((status) => (
                <span
                  key={status}
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-sm font-medium ${statusPillClass(status)}`}
                >
                  {status}: {statusSummary.counts[status]}
                </span>
              ))}
            {Object.keys(statusSummary.counts)
              .filter((status) => !statusOrder.includes(status))
              .map((status) => (
                <span
                  key={status}
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusPillClass(status)}`}
                >
                  {status}: {statusSummary.counts[status]}
                </span>
              ))}
          </div>
        )}

        <DataTable
          columns={columnDefs}
          data={traceTreeData}
          groupBy={groupByConfig}
          loading={traceConversationsLoading}
          loadingMessage="Loading recent conversations..."
          noDataMessage={
            generator.id
              ? "No conversations found for this scenario."
              : "Select a generator to see conversations."
          }
          searchLabel="Find Conversations..."
          enableColumnVisibility
          enableSearch
          hiddenCols={actualHiddenCols}
          onColumnVisibilityChange={(vis) => setHiddenCols(Object.entries(vis).filter(([, v]) => !v).map(([id]) => id))}
          stickyHeader
          actions={[
            {
              label: "Review",
              onClick: (row) => openReview(row.conversationId),
              icon: 'MagnifyingGlassIcon',
              hideWhen: (row) => !row.conversationId
            }
          ]}
          groupedRowActions={renderGroupedRowActions}
          groupedRowLabel={renderGroupedRowLabel}
        />
      </div>
    </div>
  );
}
